import { ApiError, NetworkError } from '@/api/client';
import type { TranslateFn } from '@/i18n/LanguageContext';
import type { TranslationKey } from '@/i18n/dictionaries/en';
import type { SystemLocationKind, ZonePurpose } from '@/api/types';

/** One key per submission, kept until it succeeds - a retried click can never create a second warehouse/zone. */
export function newKey(): string {
  return crypto.randomUUID();
}

/**
 * What a zone's cells can be used for. Mirrors the server's mapping
 * (ADR-083): storage/FBS/other cells are BIN locations, defect/quarantine
 * cells are QUARANTINE locations. Shown so nobody expects an FBS cell to do
 * something only a separate FBS engine could.
 */
export const PURPOSE_META: Record<ZonePurpose, { label: TranslationKey; help: TranslationKey; receive: boolean; store: boolean; pick: boolean; isolate: boolean }> = {
  STORAGE: { label: 'zones.purpose.STORAGE', help: 'zones.help.STORAGE', receive: false, store: true, pick: true, isolate: false },
  FBS: { label: 'zones.purpose.FBS', help: 'zones.help.FBS', receive: false, store: true, pick: true, isolate: false },
  OTHER: { label: 'zones.purpose.OTHER', help: 'zones.help.OTHER', receive: false, store: true, pick: true, isolate: false },
  DEFECT: { label: 'zones.purpose.DEFECT', help: 'zones.help.DEFECT', receive: true, store: true, pick: false, isolate: true },
  QUARANTINE: { label: 'zones.purpose.QUARANTINE', help: 'zones.help.QUARANTINE', receive: true, store: true, pick: false, isolate: true },
};

export const SYSTEM_KIND_META: Record<SystemLocationKind, { label: TranslationKey; help: TranslationKey }> = {
  RECEIVING: { label: 'warehouses.sys.RECEIVING', help: 'warehouses.sysHelp.RECEIVING' },
  STAGING: { label: 'warehouses.sys.STAGING', help: 'warehouses.sysHelp.STAGING' },
  PACKING: { label: 'warehouses.sys.PACKING', help: 'warehouses.sysHelp.PACKING' },
  QUARANTINE: { label: 'warehouses.sys.QUARANTINE', help: 'warehouses.sysHelp.QUARANTINE' },
  VIRTUAL: { label: 'warehouses.sys.VIRTUAL', help: 'warehouses.sysHelp.VIRTUAL' },
};

/** Common IANA zones for the partner's region; the browser's own zone is offered first. */
export const TIME_ZONES = [
  'Europe/Riga', 'Europe/Vilnius', 'Europe/Tallinn', 'Europe/Warsaw', 'Europe/Berlin', 'Europe/Belgrade',
  'Europe/Helsinki', 'Europe/Kyiv', 'Europe/Moscow', 'Europe/Istanbul', 'Europe/London', 'Asia/Almaty', 'Asia/Tashkent', 'Asia/Dubai',
];

/** A plain sentence for the errors these screens can hit; null = let the generic banner handle it. */
export function describeSetupError(t: TranslateFn, err: unknown): string | null {
  if (err instanceof NetworkError) return t('warehouses.err.network');
  if (!(err instanceof ApiError)) return null;
  const p = err.params as Record<string, string | undefined>;
  if (err.code === 'COMMON.CONFLICT' && p.field === 'code') return t('code.takenOnSubmit', { code: p.code ?? '', suggestion: p.suggestion ?? '' });
  if (err.code === 'WAREHOUSE_SETUP.CODE_TAKEN' && p.field === 'locationCode') return t('cells.err.codeTaken', { code: p.code ?? '' });
  if (err.code === 'WAREHOUSE_SETUP.CODE_INVALID' && p.field === 'locationCode') return t('cells.err.codeInvalid');
  if (err.code === 'LOCATION.HAS_STOCK') return t('cells.archiveBlocked.STOCK');
  if (err.code === 'LOCATION.HAS_OPEN_WORK') return p.reason === 'RESERVED' ? t('cells.err.reserved') : t('cells.archiveBlocked.OPEN_WORK');
  if (err.code === 'LOCATION.IN_USE') return t(p.reason === 'STOCK' ? 'cells.deleteBlocked.STOCK' : 'cells.deleteBlocked.HISTORY');
  if (err.code === 'LOCATION.SYSTEM_PROTECTED') return t('cells.err.system');
  if (err.code === 'LOCATION.ARCHIVED') return t('cells.err.archived');
  if (err.code === 'ZONE.PURPOSE_LOCKED') return t('cells.err.purposeLocked');
  if (err.code === 'WAREHOUSE_SETUP.CODE_TAKEN' && p.field === 'cells') return t('zones.err.cellsTaken', { codes: p.codes ?? '', count: p.count ?? '' });
  if (err.code === 'WAREHOUSE_SETUP.CODE_TAKEN') return t('zones.err.zoneCodeTaken', { code: p.code ?? '' });
  if (err.code === 'WAREHOUSE_SETUP.CODE_INVALID') return p.field === 'zoneCode' ? t('zones.err.zoneCodeInvalid') : t('code.formatHint');
  if (err.code === 'WAREHOUSE_SETUP.CELL_RANGE_INVALID') return t('zones.err.range', { max: p.max ?? '500' });
  if (err.code === 'WAREHOUSE_SETUP.ORG_ADMIN_REQUIRED') return t('warehouses.orgAdminOnly');
  if (err.code === 'MEMBERSHIP.OUT_OF_SCOPE') return t('warehouses.err.outOfScope');
  if (err.code === 'ENTITLEMENT.QUOTA_EXCEEDED') return t('warehouses.err.quota');
  if (err.code === 'COMMON.VALIDATION_FAILED' && p.field === 'timeZone') return t('warehouses.err.timeZone');
  if (err.code === 'COMMON.VALIDATION_FAILED' && p.reason === 'TOO_MANY_LABELS') return t('labels.tooMany', { max: p.max ?? '500' });
  if (err.code === 'COMMON.VALIDATION_FAILED' && p.reason === 'NOTHING_TO_PRINT') return t('labels.nothing');
  if (err.status === 400) return t('warehouses.err.checkFields');
  return null;
}
